import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Zap, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Real-time password match validation
  const passwordsMatch = password === confirmPassword;
  const showMismatchError = confirmPassword.length > 0 && !passwordsMatch;
  const isFormValid = name && email && password.length >= 6 && passwordsMatch && confirmPassword.length > 0 && agreedToTerms;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Use and Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await register({ name, email, password });
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F4F8] to-[#E6EFF8] flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white border border-slate-200 shadow-lg rounded-xl animate-fade-in" data-testid="register-card">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-flex items-center gap-2 justify-center mb-4">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </Link>
          <CardTitle className="text-2xl text-slate-900" style={{ fontFamily: "Public Sans" }}>Create Account</CardTitle>
          <CardDescription className="text-slate-500">Join thousands of NHS professionals</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input id="name" placeholder="Dr. Jane Smith" className="pl-10" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input id="email" type="email" placeholder="you@nhs.net" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input id="password" type="password" placeholder="Min 6 characters" className="pl-10" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input 
                  id="confirmPassword" 
                  type="password" 
                  placeholder="Re-enter password" 
                  className={`pl-10 ${showMismatchError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  required 
                  data-testid="register-confirm-password-input" 
                />
              </div>
              {showMismatchError && (
                <p className="text-sm text-red-600 flex items-center gap-1" data-testid="password-mismatch-error">
                  <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                  Passwords do not match
                </p>
              )}
              {confirmPassword.length > 0 && passwordsMatch && (
                <p className="text-sm text-green-600 flex items-center gap-1" data-testid="password-match-success">
                  <span className="inline-block w-1 h-1 bg-green-600 rounded-full"></span>
                  Passwords match
                </p>
              )}
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="terms" 
                checked={agreedToTerms} 
                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                data-testid="register-terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
                I agree to the{" "}
                <Link to="/terms-of-use" target="_blank" className="text-[#003087] hover:underline">Terms of Use</Link>
                {" "}and{" "}
                <Link to="/privacy-policy" target="_blank" className="text-[#003087] hover:underline">Privacy Policy</Link>
              </label>
            </div>
            <Button type="submit" className="w-full bg-[#003087] text-white hover:bg-[#002366] disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !isFormValid} data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account? <Link to="/login" className="text-[#003087] font-medium hover:underline" data-testid="register-login-link">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
